import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import type { DetailPanelItemType } from '../../../deals/components/forms/parts/DetailPanel.tsx'
import { Form, Row, Col, Card, Spin, Tabs, Tooltip, Space } from 'antd'
import Button from '../../../../core/components/Button.jsx'
import { RecordFormDeleteEllipses } from '../../../../core/components/RecordFormDeleteEllipses.tsx'
import { AgentProvenanceTag } from '../../../../core/components/AgentProvenanceTag.tsx'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { selectCompanyById } from '../../../../state_manager/state_update_actions/companies/selectors.ts'
import { updateCompanyService } from '../../../../state_manager/state_update_actions/companies/company_services.ts'
import {
  updateCompanyRecord,
  removeCompanyFromList
} from '../../../../state_manager/state_update_actions/companies/index.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage,
  removeActiveRecord
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { AccountRecord } from '../../../../state_manager/data_models/account_record.ts'
import { CompanyTeamEntryFields } from '../../../../state_manager/data_models/company_team_entry.ts'
import { RELATIONSHIP_CATEGORY_FOUNDER } from '../../../../state_manager/globals.ts'
import {
  accountHasPortfolioRole,
  accountHasCoInvestorRole,
  accountHasLpRole,
  accountHasDealRole
} from '../../utils/accountRoles.ts'
import { UNKNOWN_CONTACT, CONFIRM_DELETE_COMPANY_TITLE } from '../../../../state_manager/constants/strings.ts'
import { fetchCompanyById, deleteCompany } from '../../services/companiesService.ts'
import { handleFieldChange as handleBusinessRules } from '../../utils/companyFormBusinessRules.ts'
import { DashboardTab } from './tabs/DashboardTab.tsx'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { removeTab, selectActiveTabKey, updateTabPath } from '../../../../store/slices/tabSlice'
import { stripTrailingFormRouteSegments } from '../../../../core/contexts/DrawerContext.jsx'
import { DetailsTab } from './tabs/DetailsTab.tsx'
import { ContactsTab } from './tabs/ContactsTab.tsx'
import { HoldingsTab } from './tabs/HoldingsTab.tsx'
import { FundsTab } from './tabs/FundsTab.tsx'
import { RelationshipTab } from './tabs/RelationshipTab.tsx'
import AccountDealsRoundsTab from '../../../deal_execution/components/AccountDealsRoundsTab.tsx'
import CapTableTab from '../../../deal_execution/components/CapTableTab.tsx'
import LegalRightsTab from '../../../deal_execution/components/LegalRightsTab.tsx'
import AccountCoInvestmentsTab from '../../../deal_execution/components/AccountCoInvestmentsTab.tsx'
import { AccountTopicsTab } from '../../../distribution_lists/components/AccountTopicsTab.tsx'
import { ChangeLogTab } from '../../../change_log/components/ChangeLogTab.tsx'
import { AgentTab } from '../../../internal_agents/components/AgentTab.tsx'
import AccountDocumentsTab from '../AccountDocumentsTab.tsx'

interface FullCompanyFormProps {
  recordId?: string | null
  isNew?: boolean
}

export default function FullCompanyForm({ recordId: propRecordId, isNew: propIsNew }: FullCompanyFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new'

  const companiesState = useSelector((state: any) => state.companies)
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const companyFromActiveRecords = useSelector((state: any) =>
    recordId && !isNew ? selectActiveRecordById(state, recordId) : null
  )
  const companyFromState = useSelector((state: any) => (recordId && !isNew ? selectCompanyById(state, recordId) : null))
  const currentCompany = companyFromActiveRecords || companyFromState

  const companyTeamDataKey = useMemo(() => {
    const team = currentCompany?.prog__company_team
    if (!team || !Array.isArray(team)) return null
    return JSON.stringify(
      team.map((m: any) => ({
        id: m[CompanyTeamEntryFields.contact_accounts__id],
        contactId: m[CompanyTeamEntryFields.contacts__id],
        founder: m[CompanyTeamEntryFields.prog__founder],
        current: m[CompanyTeamEntryFields.contact_accounts__current]
      }))
    )
  }, [currentCompany?.prog__company_team])

  const [form] = Form.useForm()
  const [tasks, setTasks] = useState<any[]>([])
  const [interactions, setInteractions] = useState<any[]>([])
  const [companyTeam, setCompanyTeam] = useState<any[]>([])

  const [companyTeamMembersUnmerged, setCompanyTeamMembersUnmerged] = useState<any[]>([])

  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [selectedItemType, setSelectedItemType] = useState<DetailPanelItemType | null>(null)
  const [isFetchingCompany, setIsFetchingCompany] = useState(false)
  const [isDeletingCompany, setIsDeletingCompany] = useState(false)
  const activeTabKey = useSelector((state: any) => selectActiveTabKey(state))
  const hasAttemptedFetch = useRef(false)
  const companiesStateRef = useRef(companiesState)
  const refreshInteractionsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshTasksRef = useRef<(() => Promise<void>) | null>(null)
  const refreshInvestmentMeasuresRef = useRef<(() => Promise<void>) | null>(null)
  const refreshHoldingsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshCommitmentsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshCapTableRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLegalRightsRef = useRef<(() => Promise<void>) | null>(null)
  // Deals & Rounds was the one data tab missing from this fan-out — a deal
  // created elsewhere never appeared there until a hard refresh.
  const refreshDealsRoundsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshDocumentsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentPanelsRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAgentTabRef = useRef<(() => Promise<void>) | null>(null)
  const refreshLpAgentTabRef = useRef<(() => Promise<void>) | null>(null)
  const refreshRelationshipTabRef = useRef<(() => Promise<void>) | null>(null)

  const dashboardTabKey = `dashboard-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const detailsTabKey = `details-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const contactsTabKey = `contacts-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const holdingsTabKey = `holdings-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const fundsTabKey = `funds-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const dealsRoundsTabKey = `deals-rounds-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const agentTabKey = `agent-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const lpAgentTabKey = `lp-agent-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const capTableTabKey = `cap-table-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const legalRightsTabKey = `legal-rights-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const documentsTabKey = `documents-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const coInvestmentsTabKey = `co-investments-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const topicsTabKey = `account-topics-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  const changeLogTabKey = `change-log-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`
  // Deep-link tab target, read from the query string set by openFormInTab
  // callers — the same contract the deal form honours (?tab=execution from
  // the stage-alerts bell). Agent signal / memory citations arrive with
  // ?tab=dashboard&interaction=<id>: the timeline lives on the Dashboard, so
  // a form open on the Agent tab has to switch back for the citation to open.
  // Unknown or absent values fall back to the Dashboard.
  const TAB_QUERY_KEYS = [
    'dashboard',
    'details',
    'contacts',
    'holdings',
    'funds',
    'deals-rounds',
    'agent',
    'lp-agent',
    'cap-table',
    'legal-rights',
    'documents',
    'co-investments',
    'account-topics'
  ]
  const resolveTabFromQuery = () => {
    if (isNew) return dashboardTabKey
    const requested = new URLSearchParams(window.location.search).get('tab')
    if (!requested || !TAB_QUERY_KEYS.includes(requested)) return dashboardTabKey
    return `${requested}-${recordId || 'new'}-edit`
  }
  const [activeTab, setActiveTab] = useState<string>(resolveTabFromQuery)
  const location = useLocation()

  // Re-resolve when the query string changes while this form is already
  // mounted (a citation on an open account form). Guards as on the deal
  // form: only when the URL points at THIS record (inactive form tabs stay
  // mounted and the global location belongs to the active tab), and only when
  // a ?tab= param is present — its absence means the deep link was consumed.
  useEffect(() => {
    if (isNew || !recordId) return
    if (!location.pathname.endsWith(`/forms/full/${recordId}`)) return
    if (!new URLSearchParams(location.search).get('tab')) return
    setActiveTab(resolveTabFromQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  // Holdings tab — and the Latest Valuation / Investment Summary dashboard
  // sections — are portfolio-only. Gate on the shared role predicate.
  const showHoldingsTab = useMemo(() => {
    if (!currentCompany || isNew) return false
    return accountHasPortfolioRole(currentCompany)
  }, [currentCompany, isNew])

  // Co-investments tab: only for accounts carrying the Co-investor role
  // (added by the role engine when linked as a cap-table holder).
  const showCoInvestmentsTab = useMemo(() => {
    if (!currentCompany || isNew) return false
    return accountHasCoInvestorRole(currentCompany)
  }, [currentCompany, isNew])

  // Funds tab: every LP-role account, the way Holdings gates on the portfolio
  // role (owner, 2026-09-06). It was commitments_count > 0 alone, which hid
  // the tab from an LP with live fundraising processes and no commitment yet —
  // exactly the LP whose Fundraising History, now the tab's second section,
  // says the most. The count arm survives so a commitment-bearing account that
  // somehow lacks the LP role keeps its commitments.
  const showFundsTab = useMemo(() => {
    if (!currentCompany || isNew) return false
    if (accountHasLpRole(currentCompany)) return true
    const count = Number(currentCompany[AccountRecordFields.prog__commitments_count] ?? 0)
    return Number.isFinite(count) && count > 0
  }, [currentCompany, isNew])

  // Cap Table and Legal Rights describe a HOLDING, so they follow the same
  // portfolio gate as Holdings (owner, 2026-09-03).
  const showCapTableTab = showHoldingsTab
  const showLegalRightsTab = showHoldingsTab

  // Deals & Rounds is wider: every account the firm has EVALUATED, i.e. the
  // portfolio roles plus the origination outcomes (Pipeline / Passed /
  // Passed - Track / Lost). An advisory firm or a plain LP has no deal
  // history to show.
  const showDealsRoundsTab = useMemo(() => {
    if (!currentCompany || isNew) return false
    return accountHasDealRole(currentCompany)
  }, [currentCompany, isNew])

  useEffect(() => {
    setActiveTab(resolveTabFromQuery())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardTabKey])

  // A gated tab can disappear under the user: the role engine reconciles
  // account_roles on a patch (a holdings or cap-table change can drop the
  // Portfolio Company role), and antd renders an empty panel when activeKey
  // matches no item. Fall back to the Dashboard rather than a blank tab.
  useEffect(() => {
    const gated: Array<[string, boolean]> = [
      [holdingsTabKey, showHoldingsTab],
      [coInvestmentsTabKey, showCoInvestmentsTab],
      [fundsTabKey, showFundsTab],
      [dealsRoundsTabKey, showDealsRoundsTab],
      [capTableTabKey, showCapTableTab],
      [legalRightsTabKey, showLegalRightsTab]
    ]
    if (gated.some(([key, visible]) => key === activeTab && !visible)) {
      setActiveTab(dashboardTabKey)
    }
  }, [
    activeTab,
    dashboardTabKey,
    holdingsTabKey,
    coInvestmentsTabKey,
    fundsTabKey,
    dealsRoundsTabKey,
    capTableTabKey,
    legalRightsTabKey,
    showHoldingsTab,
    showCoInvestmentsTab,
    showFundsTab,
    showDealsRoundsTab,
    showCapTableTab,
    showLegalRightsTab
  ])

  const handlePatchSuccess = useCallback(
    (patchRecordId: string, updates: Record<string, any>) => {
      if (!patchRecordId) return

      if (AccountRecordFields.accounts__business_country_code in updates) {
        const countryCode = updates[AccountRecordFields.accounts__business_country_code]
        if (countryCode && countriesData.length > 0) {
          updates[AccountRecordFields.accounts__business_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          updates[AccountRecordFields.accounts__business_country_label] = null
        }
      }

      if (AccountRecordFields.accounts__office_country_code in updates) {
        const officeCountryCode = updates[AccountRecordFields.accounts__office_country_code]
        if (officeCountryCode && countriesData.length > 0) {
          updates[AccountRecordFields.accounts__office_country_code__label] = countriesData.find(
            (country: any) => country.code === officeCountryCode
          )?.name
        } else {
          updates[AccountRecordFields.accounts__office_country_code__label] = null
        }
      }

      dispatch(updateCompanyRecord({ companyId: patchRecordId, updates }))
    },
    [dispatch, countriesData]
  )

  useEffect(() => {
    companiesStateRef.current = companiesState
  }, [companiesState])

  const [formData, setFormData] = useState<Record<string, any>>({
    [AccountRecordFields.accounts__name]: '',
    [AccountRecordFields.accounts__legal_name]: '',
    [AccountRecordFields.accounts__industry]: '',
    [AccountRecordFields.accounts__investor_classification_id]: null,
    [AccountRecordFields.accounts__website]: '',
    [AccountRecordFields.accounts__description]: '',
    [AccountRecordFields.accounts__incorporation]: '',
    [AccountRecordFields.accounts__founded_in_city_label]: '',
    [AccountRecordFields.accounts__founded_in_stateprovince]: '',
    [AccountRecordFields.accounts__founded_in_country_code]: '',
    [AccountRecordFields.prog__account_roles]: [],
    [AccountRecordFields.prog__rounds]: [],
    [AccountRecordFields.accounts__business_address_1]: '',
    [AccountRecordFields.accounts__business_address_2]: '',
    [AccountRecordFields.accounts__business_city_label]: '',
    [AccountRecordFields.accounts__business_stateprovince]: '',
    [AccountRecordFields.accounts__business_postalcode]: '',
    [AccountRecordFields.accounts__business_country_label]: '',
    [AccountRecordFields.accounts__same_as_business_address]: true,
    [AccountRecordFields.accounts__office_address_1]: '',
    [AccountRecordFields.accounts__office_address_2]: '',
    [AccountRecordFields.accounts__office_city_code__label]: '',
    [AccountRecordFields.accounts__office_stateprovince]: '',
    [AccountRecordFields.accounts__office_postalcode]: '',
    [AccountRecordFields.accounts__office_country_code__label]: '',
    introduced_by: [],
    sourced_by: []
  })

  useEffect(() => {
    hasAttemptedFetch.current = false
  }, [recordId])

  const previousRecordIdRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const recordIdString = recordId?.toString()
    const previousRecordIdString = previousRecordIdRef.current?.toString()

    if (!isNew && recordIdString && recordIdString !== previousRecordIdString) {
      const currentCompanyNow = companyFromActiveRecords || companyFromState

      if (currentCompanyNow) {
        hasAttemptedFetch.current = true
        dispatch(setActiveRecord({ uuid: recordIdString, record: currentCompanyNow }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
      } else if (!hasAttemptedFetch.current) {
        const currentToken = getAccessToken(store.getState().auth)
        if (currentToken) {
          hasAttemptedFetch.current = true
          setIsFetchingCompany(true)
          fetchCompanyById(recordIdString, currentToken)
            .then((response) => {
              if (response.data && !response.error) {
                const fetchedCompany = response.data as AccountRecord
                dispatch(setActiveRecord({ uuid: recordIdString, record: fetchedCompany }))
                dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
              } else if (response.error) {
                console.error('Error fetching company:', response.error)
              }
            })
            .catch((error) => {
              console.error('Error fetching company:', error)
            })
            .finally(() => {
              setIsFetchingCompany(false)
            })
        }
      }
      previousRecordIdRef.current = recordId
    }
  }, [recordId, isNew, dispatch, companyFromActiveRecords, companyFromState])

  useEffect(() => {
    return () => {
      if (recordId && !isNew) {
        const companyIdString = recordId.toString()
        dispatch(unregisterRecordUsage({ uuid: companyIdString, context: 'fullForm' }))
      }
    }
  }, [recordId, isNew, dispatch])

  const handleDeleteCompany = useCallback(async () => {
    if (!recordId || isNew) return
    const token = getAccessToken(store.getState().auth)
    if (!token) {
      showErrorToast('Not authenticated')
      return
    }
    setIsDeletingCompany(true)
    try {
      await deleteCompany(recordId, token)
      toast.success('Record deleted')
      const id = String(recordId)
      dispatch(removeCompanyFromList({ companyId: id }))
      dispatch(removeActiveRecord({ uuid: id }))
      dispatch(unregisterRecordUsage({ uuid: id, context: 'fullForm' }))
      window.isClosingTab = true
      dispatch(removeTab({ key: activeTabKey }))
      const nextState = store.getState().tabs
      const nextTab = nextState.tabs.find((t: { key: string }) => t.key === nextState.activeTabKey)
      if (nextTab?.path) {
        const pathWithoutFormShell = stripTrailingFormRouteSegments(nextTab.path)
        if (pathWithoutFormShell !== nextTab.path) {
          dispatch(updateTabPath({ key: nextTab.key, path: pathWithoutFormShell }))
        }
        navigate(pathWithoutFormShell, { replace: true })
      }
      setTimeout(() => {
        window.isClosingTab = false
      }, 150)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to delete company')
    } finally {
      setIsDeletingCompany(false)
    }
  }, [recordId, isNew, dispatch, navigate, activeTabKey])

  const handleRefresh = useCallback(async () => {
    if (!recordId || isNew) {
      return
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (!currentToken) {
      return
    }

    setIsFetchingCompany(true)
    try {
      const result = await fetchCompanyById(recordId, currentToken)
      if (result && result.data) {
        const recordIdString = recordId.toString()
        dispatch(setActiveRecord({ uuid: recordIdString, record: result.data }))
        dispatch(registerRecordUsage({ uuid: recordIdString, context: 'fullForm' }))
        dispatch(updateCompanyRecord({ companyId: recordIdString, updates: result.data }))
        toast.success('Refreshed Successfully')

        if (refreshInteractionsRef.current) {
          await refreshInteractionsRef.current()
        }

        if (refreshTasksRef.current) {
          await refreshTasksRef.current()
        }

        if (refreshInvestmentMeasuresRef.current) {
          await refreshInvestmentMeasuresRef.current()
        }

        if (refreshHoldingsRef.current) {
          await refreshHoldingsRef.current()
        }

        if (refreshCapTableRef.current) {
          await refreshCapTableRef.current()
        }

        if (refreshLegalRightsRef.current) {
          await refreshLegalRightsRef.current()
        }
        if (refreshDealsRoundsRef.current) {
          await refreshDealsRoundsRef.current()
        }
        if (refreshDocumentsRef.current) {
          await refreshDocumentsRef.current()
        }

        // Commitments registers its refresher below but was never driven from
        // here, so the tab kept showing pre-refresh figures.
        if (refreshCommitmentsRef.current) {
          await refreshCommitmentsRef.current()
        }

        // Internal-agent surfaces: the Dashboard panels (states, crit signals,
        // brief, KPIs) and the Agent tab. Both fetch straight from the agent
        // endpoints rather than the company record, so nothing above reloads
        // them.
        if (refreshAgentPanelsRef.current) {
          await refreshAgentPanelsRef.current()
        }

        if (refreshRelationshipTabRef.current) {
          await refreshRelationshipTabRef.current()
        }
        if (refreshAgentTabRef.current) {
          await refreshAgentTabRef.current()
        }
        if (refreshLpAgentTabRef.current) {
          await refreshLpAgentTabRef.current()
        }
      }
    } catch (error) {
      console.error('Error refreshing company:', error)
    } finally {
      setIsFetchingCompany(false)
    }
  }, [recordId, isNew, dispatch])

  useEffect(() => {
    if (currentCompany && !isNew) {
      let introducedByContacts: any[] = []
      const introducedByDetail =
        currentCompany[AccountRecordFields.contact_accounts__introduced_by] || currentCompany.introduced_by_detail
      if (introducedByDetail && Array.isArray(introducedByDetail)) {
        introducedByContacts = introducedByDetail.map((contact: any) => ({
          value: contact.id,
          label:
            contact.full_name ||
            contact.display_name ||
            `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
            contact.email ||
            UNKNOWN_CONTACT,
          data: contact
        }))
      }

      let sourcedByContacts: any[] = []
      const sourcedByDetails =
        currentCompany[AccountRecordFields.contact_accounts__sourced_by] || currentCompany.sourced_by_details
      if (sourcedByDetails && Array.isArray(sourcedByDetails)) {
        sourcedByContacts = sourcedByDetails.map((contact: any) => ({
          value: contact.id,
          label:
            contact.full_name ||
            contact.display_name ||
            `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
            contact.email ||
            UNKNOWN_CONTACT,
          data: contact
        }))
      }

      const rolesArray = currentCompany[AccountRecordFields.prog__account_roles] || []
      const roleIds = Array.isArray(rolesArray)
        ? rolesArray
            .map((role: any) => {
              if (typeof role === 'string') return role
              if (role && typeof role === 'object' && role.account_roles__account_role_choice_id) {
                return role.account_roles__account_role_choice_id
              }
              return null
            })
            .filter((id: any): id is string => id !== null)
        : []

      const initialData: Record<string, any> = {
        [AccountRecordFields.accounts__name]: currentCompany[AccountRecordFields.accounts__name] || '',
        [AccountRecordFields.accounts__legal_name]: currentCompany[AccountRecordFields.accounts__legal_name] || '',
        [AccountRecordFields.accounts__industry]: currentCompany[AccountRecordFields.accounts__industry] || '',
        [AccountRecordFields.accounts__investor_classification_id]:
          currentCompany[AccountRecordFields.accounts__investor_classification_id] ?? null,
        [AccountRecordFields.accounts__website]: currentCompany[AccountRecordFields.accounts__website] || '',
        [AccountRecordFields.accounts__description]: currentCompany[AccountRecordFields.accounts__description] || '',
        [AccountRecordFields.accounts__incorporation]:
          currentCompany[AccountRecordFields.accounts__incorporation] || '',
        [AccountRecordFields.accounts__founded_in_city_label]:
          currentCompany[AccountRecordFields.accounts__founded_in_city_label] || '',
        [AccountRecordFields.accounts__founded_in_stateprovince]:
          currentCompany[AccountRecordFields.accounts__founded_in_stateprovince] || '',
        [AccountRecordFields.accounts__founded_in_country_code]:
          currentCompany[AccountRecordFields.accounts__founded_in_country_code] || '',
        [AccountRecordFields.prog__account_roles]: roleIds,
        [AccountRecordFields.prog__rounds]: currentCompany[AccountRecordFields.prog__rounds] || [],
        [AccountRecordFields.accounts__business_address_1]:
          currentCompany[AccountRecordFields.accounts__business_address_1] || '',
        [AccountRecordFields.accounts__business_address_2]:
          currentCompany[AccountRecordFields.accounts__business_address_2] || '',
        [AccountRecordFields.accounts__business_city_label]:
          currentCompany[AccountRecordFields.accounts__business_city_label] || '',
        [AccountRecordFields.accounts__business_stateprovince]:
          currentCompany[AccountRecordFields.accounts__business_stateprovince] || '',
        [AccountRecordFields.accounts__business_postalcode]:
          currentCompany[AccountRecordFields.accounts__business_postalcode] || '',
        [AccountRecordFields.accounts__business_country_label]:
          currentCompany[AccountRecordFields.accounts__business_country_label] || '',
        [AccountRecordFields.accounts__business_country_code]:
          currentCompany[AccountRecordFields.accounts__business_country_code] || '',
        [AccountRecordFields.accounts__same_as_business_address]:
          currentCompany[AccountRecordFields.accounts__same_as_business_address] !== undefined
            ? currentCompany[AccountRecordFields.accounts__same_as_business_address]
            : true,
        [AccountRecordFields.accounts__office_address_1]:
          currentCompany[AccountRecordFields.accounts__office_address_1] || '',
        [AccountRecordFields.accounts__office_address_2]:
          currentCompany[AccountRecordFields.accounts__office_address_2] || '',
        [AccountRecordFields.accounts__office_city_code__label]:
          currentCompany[AccountRecordFields.accounts__office_city_code__label] || '',
        [AccountRecordFields.accounts__office_stateprovince]:
          currentCompany[AccountRecordFields.accounts__office_stateprovince] || '',
        [AccountRecordFields.accounts__office_postalcode]:
          currentCompany[AccountRecordFields.accounts__office_postalcode] || '',
        [AccountRecordFields.accounts__office_country_code__label]:
          currentCompany[AccountRecordFields.accounts__office_country_code__label] || '',
        [AccountRecordFields.accounts__office_country_code]:
          currentCompany[AccountRecordFields.accounts__office_country_code] || '',
        introduced_by: introducedByContacts,
        sourced_by: sourcedByContacts
      }
      setFormData(initialData)
      form.setFieldsValue(initialData)

      const companyTeam = currentCompany.prog__company_team
      if (companyTeam && Array.isArray(companyTeam) && companyTeam.length > 0) {
        const unmergedTeamMembers = companyTeam.map((member: any) => {
          const contactId = member[CompanyTeamEntryFields.contacts__id]
          const isFounder = member[CompanyTeamEntryFields.prog__founder] || false
          const relationshipId = member[CompanyTeamEntryFields.contact_accounts__id]
          const isCurrent = member[CompanyTeamEntryFields.contact_accounts__current] !== false

          const contactData = {
            display_name: member[CompanyTeamEntryFields.prog__display_name],
            first_name: member[CompanyTeamEntryFields.contacts__first_name],
            last_name: member[CompanyTeamEntryFields.contacts__last_name],
            email: member[CompanyTeamEntryFields.contacts__email],
            mobile_phone: member[CompanyTeamEntryFields.contacts__mobile_phone],
            preferred_name: member[CompanyTeamEntryFields.contacts__preferred_name],
            photo_url: member[CompanyTeamEntryFields.contacts__photo_url]
          }

          const relationshipCategoryId = member[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          const category =
            relationshipCategoryId && relationshipCategoryId !== RELATIONSHIP_CATEGORY_FOUNDER
              ? relationshipCategoryId
              : null

          return {
            key: relationshipId || `team_${contactId}_${isFounder ? 'founder' : 'employee'}`,
            employeeRelationshipId: !isFounder ? relationshipId : null,
            founderRelationshipId: isFounder ? relationshipId : null,
            contact: {
              value: contactId,
              label:
                contactData.display_name ||
                `${contactData.first_name || ''} ${contactData.last_name || ''}`.trim() ||
                contactData.email ||
                UNKNOWN_CONTACT,
              data: contactData
            },
            founder: isFounder,
            current: isCurrent,
            category: category,
            jobTitle: member[CompanyTeamEntryFields.contact_accounts__title] || '',
            email: contactData.email || '',
            contactNumber: contactData.mobile_phone || '',
            photo_url: contactData.photo_url || null
          }
        })
        setCompanyTeamMembersUnmerged(unmergedTeamMembers)
      } else {
        setCompanyTeamMembersUnmerged([])
      }

      if (isNew) {
        setCompanyTeam([])
        setCompanyTeamMembersUnmerged([])
      }

      const newCompanyTeam: any[] = []

      const introducedByDetailForTeam =
        currentCompany[AccountRecordFields.contact_accounts__introduced_by] || currentCompany.introduced_by_detail
      if (introducedByDetailForTeam && Array.isArray(introducedByDetailForTeam)) {
        introducedByDetailForTeam.forEach((contact: any, index: number) => {
          const displayName = contact.preferred_name || contact.first_name || ''
          const fullName = contact.last_name ? `${displayName} ${contact.last_name}`.trim() : displayName
          newCompanyTeam.push({
            id: `intro_${contact.id}_${index}`,
            contactId: contact.id,
            role: 'introduced_by',
            name: fullName || contact.email || UNKNOWN_CONTACT,
            title: '',
            email: contact.email || '',
            avatar: null,
            initials: (contact.first_name?.charAt(0) || '') + (contact.last_name?.charAt(0) || ''),
            dateAdded: contact.relationship_from || currentCompany.date_created || new Date().toISOString(),
            userType: 'contact'
          })
        })
      }

      const sourcedByDetailsForTeam =
        currentCompany[AccountRecordFields.contact_accounts__sourced_by] || currentCompany.sourced_by_details
      if (sourcedByDetailsForTeam && Array.isArray(sourcedByDetailsForTeam)) {
        sourcedByDetailsForTeam.forEach((contact: any, index: number) => {
          const displayName = contact.preferred_name || contact.first_name || ''
          const fullName = contact.last_name ? `${displayName} ${contact.last_name}`.trim() : displayName
          newCompanyTeam.push({
            id: `sourced_${contact.id}_${index}`,
            contactId: contact.id,
            role: 'sourced_by',
            name: fullName || contact.email || UNKNOWN_CONTACT,
            title: '',
            email: contact.email || '',
            avatar: null,
            initials: (contact.first_name?.charAt(0) || '') + (contact.last_name?.charAt(0) || ''),
            dateAdded: contact.relationship_from || currentCompany.date_created || new Date().toISOString(),
            userType: 'contact'
          })
        })
      }

      const companyTeamForDisplay = currentCompany.prog__company_team
      if (companyTeamForDisplay && Array.isArray(companyTeamForDisplay) && companyTeamForDisplay.length > 0) {
        companyTeamForDisplay.forEach((member: any, index: number) => {
          const displayName =
            member[CompanyTeamEntryFields.contacts__preferred_name] ||
            member[CompanyTeamEntryFields.contacts__first_name] ||
            ''
          const fullName = member[CompanyTeamEntryFields.contacts__last_name]
            ? `${displayName} ${member[CompanyTeamEntryFields.contacts__last_name]}`.trim()
            : displayName
          const role = member[CompanyTeamEntryFields.prog__founder] ? 'founder' : 'employee'
          const title = member[CompanyTeamEntryFields.contact_accounts__title] || ''

          newCompanyTeam.push({
            id: `company_team_${member[CompanyTeamEntryFields.contact_accounts__id]}_${index}`,
            contactId: member[CompanyTeamEntryFields.contacts__id],
            role: role,
            name: fullName || member[CompanyTeamEntryFields.contacts__email] || UNKNOWN_CONTACT,
            title: title,
            email: member[CompanyTeamEntryFields.contacts__email] || '',
            avatar: null,
            initials:
              (member[CompanyTeamEntryFields.contacts__first_name]?.charAt(0) || '') +
              (member[CompanyTeamEntryFields.contacts__last_name]?.charAt(0) || ''),
            dateAdded: currentCompany.date_created || new Date().toISOString(),
            userType: 'contact',
            isFounder: member[CompanyTeamEntryFields.prog__founder]
          })
        })
      }

      setCompanyTeam(newCompanyTeam)
    } else if (isNew) {
      setCompanyTeam([])
      setCompanyTeamMembersUnmerged([])
    }
  }, [currentCompany, companyTeamDataKey, isNew, form])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      const updates: Record<string, any> = {}

      if (
        field === AccountRecordFields.accounts__business_city_label ||
        field === AccountRecordFields.accounts__business_city_code
      ) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[AccountRecordFields.accounts__business_city_label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[AccountRecordFields.accounts__business_city_label] = cityObject
          }
        }

        if (countryCode) {
          updates[AccountRecordFields.accounts__business_country_code] = countryCode
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[AccountRecordFields.accounts__business_country_label] = countryLabel
            } else {
              updates[AccountRecordFields.accounts__business_country_label] = countryCode
            }
          } else {
            updates[AccountRecordFields.accounts__business_country_label] = countryCode
          }
        }
      }

      if (
        field === AccountRecordFields.accounts__business_country_code ||
        field === AccountRecordFields.accounts__business_country_label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[AccountRecordFields.accounts__business_city_code] = null
        updates[AccountRecordFields.accounts__business_city_label] = null

        if (countryCode) {
          updates[AccountRecordFields.accounts__business_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[AccountRecordFields.accounts__business_country_label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[AccountRecordFields.accounts__business_country_label] = countryName
          }
        }
      }

      if (
        field === AccountRecordFields.accounts__office_city_code__label ||
        field === AccountRecordFields.accounts__office_city_code
      ) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[AccountRecordFields.accounts__office_city_code__label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[AccountRecordFields.accounts__office_city_code__label] = cityObject
          }
        }

        if (countryCode) {
          updates[AccountRecordFields.accounts__office_country_code] = countryCode
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[AccountRecordFields.accounts__office_country_code__label] = countryLabel
            } else {
              updates[AccountRecordFields.accounts__office_country_code__label] = countryCode
            }
          } else {
            updates[AccountRecordFields.accounts__office_country_code__label] = countryCode
          }
        }
      }

      if (
        field === AccountRecordFields.accounts__office_country_code ||
        field === AccountRecordFields.accounts__office_country_code__label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[AccountRecordFields.accounts__office_city_code] = null
        updates[AccountRecordFields.accounts__office_city_code__label] = null

        if (countryCode) {
          updates[AccountRecordFields.accounts__office_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[AccountRecordFields.accounts__office_country_code__label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[AccountRecordFields.accounts__office_country_code__label] = countryName
          }
        }
      }

      if (
        field === AccountRecordFields.accounts__founded_in_city_label ||
        field === AccountRecordFields.accounts__founded_in_city_code
      ) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const state = value?.data?.state ?? null
        const cityLabel = value?.name || value?.label || ''
        const cityCode = typeof value === 'object' ? value?.value || value?.code : value

        if (cityCode !== undefined && cityCode !== null && cityCode !== '') {
          if (typeof value === 'object' && value !== null) {
            updates[AccountRecordFields.accounts__founded_in_city_label] = value
          } else {
            const cityObject = {
              value: cityCode,
              name: cityLabel || `City ${cityCode}`,
              label: cityLabel || `City ${cityCode}`,
              data: { countryCode }
            }
            updates[AccountRecordFields.accounts__founded_in_city_label] = cityObject
          }
        }

        if (state !== null) {
          updates[AccountRecordFields.accounts__founded_in_stateprovince] = state
        }

        if (countryCode) {
          updates[AccountRecordFields.accounts__founded_in_country_code] = countryCode
        }
      }

      if (field === AccountRecordFields.accounts__founded_in_country_code) {
        const countryCode = value?.value || value

        updates[AccountRecordFields.accounts__founded_in_city_code] = null
        updates[AccountRecordFields.accounts__founded_in_city_label] = null
        updates[AccountRecordFields.accounts__founded_in_stateprovince] = null

        if (countryCode) {
          updates[AccountRecordFields.accounts__founded_in_country_code] = countryCode
        }
      }

      if (field === AccountRecordFields.accounts__same_as_business_address) {
        updates[AccountRecordFields.accounts__same_as_business_address] = value
        if (value === true) {
          updates[AccountRecordFields.accounts__office_address_1] = null
          updates[AccountRecordFields.accounts__office_address_2] = null
          updates[AccountRecordFields.accounts__office_city_code] = null
          updates[AccountRecordFields.accounts__office_city_code__label] = null
          updates[AccountRecordFields.accounts__office_stateprovince] = null
          updates[AccountRecordFields.accounts__office_postalcode] = null
          updates[AccountRecordFields.accounts__office_country_code] = null
          updates[AccountRecordFields.accounts__office_country_code__label] = null
        }
      }

      if (Object.keys(updates).length > 0 && !isNew && recordId && currentCompany) {
        dispatch(updateCompanyRecord({ companyId: recordId, updates }))
      }

      setFormData((prev) => {
        const businessRuleResult = handleBusinessRules(field, value, prev, form)
        return businessRuleResult.formData
      })
    },
    [currentCompany, recordId, isNew, dispatch, countriesData, form]
  )

  const handleSaveField = async (fieldName: string, fieldValue: any) => {
    if (isNew || !recordId) {
      return
    }

    try {
      dispatch(
        updateCompanyRecord({
          companyId: recordId,
          updates: { [fieldName]: fieldValue }
        })
      )

      const currentToken = getAccessToken(store.getState().auth)
      const result = await updateCompanyService(recordId, { [fieldName]: fieldValue }, currentToken, dispatch)

      if (result.error) {
        dispatch(
          updateCompanyRecord({
            companyId: recordId,
            updates: { [fieldName]: currentCompany?.[fieldName] }
          })
        )
        throw new Error(result.error)
      }

      const companyTeamFields = ['introduced_by', 'sourced_by']
      if (companyTeamFields.includes(fieldName)) {
        const contactDetails = Array.isArray(fieldValue)
          ? fieldValue.map((contact: any) => {
              const contactData = contact.data || contact
              return {
                ...contactData,
                display_name: contact.label || contactData.display_name,
                full_name: contact.label || contactData.full_name
              }
            })
          : []

        const detailFieldMap: Record<string, string> = {
          introduced_by: 'introduced_by_detail',
          sourced_by: 'sourced_by_details'
        }

        const detailFieldName = detailFieldMap[fieldName]

        dispatch(
          updateCompanyRecord({
            companyId: recordId,
            updates: {
              [fieldName]: fieldValue,
              [detailFieldName]: contactDetails
            }
          })
        )

        const updatedFormData = {
          ...formData,
          [detailFieldName]: contactDetails
        }
        setFormData(updatedFormData)
      }

      if (result.data && typeof result.data === 'object') {
        const updates: Record<string, any> = { ...result.data }

        if (companyTeamFields.includes(fieldName)) {
          const detailFieldMap: Record<string, string> = {
            introduced_by: 'introduced_by_detail',
            sourced_by: 'sourced_by_details'
          }
          const detailFieldName = detailFieldMap[fieldName]
          const contactDetails = Array.isArray(fieldValue)
            ? fieldValue.map((contact: any) => {
                const contactData = contact.data || contact
                return {
                  ...contactData,
                  display_name: contact.label || contactData.display_name,
                  full_name: contact.label || contactData.full_name
                }
              })
            : []

          updates[detailFieldName] = contactDetails
        }

        dispatch(
          updateCompanyRecord({
            companyId: recordId,
            updates
          })
        )

        if (!companyTeamFields.includes(fieldName)) {
          const updatedFormData = {
            ...formData,
            [AccountRecordFields.accounts__business_country_label]:
              updates[AccountRecordFields.accounts__business_country_label] ||
              formData[AccountRecordFields.accounts__business_country_label],
            [AccountRecordFields.accounts__office_country_code__label]:
              updates[AccountRecordFields.accounts__office_country_code__label] ||
              formData[AccountRecordFields.accounts__office_country_code__label],
            [fieldName]: updates[fieldName] !== undefined ? updates[fieldName] : fieldValue
          }
          setFormData(updatedFormData)
          form.setFieldsValue(updatedFormData)
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save field:', error)
      }
      throw error
    }
  }

  const handleTaskAdd = useCallback((newTask?: any) => {
    if (newTask === undefined) {
      setSelectedItemType('task')
      return
    }
    const task = {
      id: Date.now().toString(),
      completed: false,
      createdDate: new Date().toISOString(),
      ...newTask
    }
    setTasks((prevTasks) => [task, ...prevTasks])
  }, [])

  const handleInteractionAdd = useCallback((newInteraction?: any) => {
    if (newInteraction === undefined) {
      setSelectedItemType('interaction')
      return
    }
    const interaction = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      ...newInteraction
    }
    setInteractions((prevInteractions) => [interaction, ...prevInteractions])
  }, [])

  const handleCompanyTeamMembersChange = useCallback((updatedCompanyTeamMembers: any[]) => {
    setCompanyTeamMembersUnmerged(updatedCompanyTeamMembers)
  }, [])

  const handleTaskSelect = useCallback((task: any) => {
    setSelectedItem(task)
    setSelectedItemType('task')
  }, [])

  const handleInteractionSelect = useCallback((interaction: any) => {
    setSelectedItem(interaction)
    setSelectedItemType('interaction')
  }, [])

  const handleDetailClose = useCallback(() => {
    setSelectedItem(null)
    setSelectedItemType(null)
  }, [])

  const shouldShowLoading = !isNew && !currentCompany && isFetchingCompany

  if (shouldShowLoading) {
    return (
      <div className='h-full'>
        <div className='w-full bg-gray-50 p-3'>
          <div className='mx-auto' style={{ maxWidth: 'calc(100vw - 24px)' }}>
            <div className='flex items-center justify-center min-h-[400px]'>
              <Spin size='large' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        data-debug='full-form-container'
        data-component='FullCompanyForm'
        data-record-id={recordId}
        data-is-new={isNew}
        data-active-tab={activeTab}
        className='h-full w-full min-h-0 flex flex-col'
      >
        <div data-debug='full-form-wrapper' className='flex-1 w-full h-full bg-gray-50 p-3 overflow-hidden'>
          <div data-debug='full-form-content' className='flex-1 flex-col w-full h-full'>
            <Card
              data-debug='full-form-main-card'
              size='small'
              className='flex-1 min-h-0 flex flex-col h-full'
              classNames={{ body: 'h-full' }}
            >
              <Form
                key={`form-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`}
                data-debug='full-form-form'
                className='h-full'
                form={form}
                layout='vertical'
                initialValues={formData}
                onValuesChange={(changedValues, allValues) => {
                  Object.keys(changedValues).forEach((key) => {
                    handleFieldChange(key, changedValues[key])
                  })
                }}
                component={false}
              >
                <Row data-debug='full-form-main-row' className='h-full' gutter={16}>
                  <Col span={24} className='h-full'>
                    <Tabs
                      key={`tabs-${recordId || 'new'}-${isNew ? 'new' : 'edit'}`}
                      data-debug='full-form-tabs'
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      type='line'
                      size='middle'
                      className='deal-form-tabs'
                      tabBarExtraContent={
                        !isNew ? (
                          <Space size='small'>
                            <AgentProvenanceTag
                              provenance={currentCompany?.[AccountRecordFields.prog__created_provenance]}
                            />
                            <RecordFormDeleteEllipses
                              confirmTitle={CONFIRM_DELETE_COMPANY_TITLE}
                              onDelete={handleDeleteCompany}
                              loading={isDeletingCompany}
                              disabled={isFetchingCompany}
                            />
                            <Tooltip title='Refresh'>
                              <Button
                                variant='default'
                                data-debug='refresh-full-form-btn'
                                size='small'
                                icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
                                onClick={handleRefresh}
                                loading={isFetchingCompany}
                                disabled={isDeletingCompany}
                                style={{ width: '32px', height: '32px', padding: 0, minWidth: 0 }}
                              />
                            </Tooltip>
                          </Space>
                        ) : null
                      }
                      items={[
                        {
                          key: dashboardTabKey,
                          label: 'Dashboard',
                          children: (
                            <div data-debug='full-form-dashboard-tab' className='h-full'>
                              <DashboardTab
                                key={dashboardTabKey}
                                currentCompany={currentCompany}
                                formData={formData}
                                recordId={recordId}
                                handleFieldChange={handleFieldChange}
                                onPatchSuccess={handlePatchSuccess}
                                handleTaskSelect={handleTaskSelect}
                                handleTaskAdd={handleTaskAdd}
                                handleInteractionSelect={handleInteractionSelect}
                                handleInteractionAdd={handleInteractionAdd}
                                handleDetailClose={handleDetailClose}
                                selectedItem={selectedItem}
                                selectedItemType={selectedItemType}
                                companyTeamMembers={companyTeamMembersUnmerged}
                                onCompanyTeamMembersChange={handleCompanyTeamMembersChange}
                                onRefreshInteractionsReady={(refreshFn) => {
                                  refreshInteractionsRef.current = refreshFn
                                }}
                                onRefreshTasksReady={(refreshFn) => {
                                  refreshTasksRef.current = refreshFn
                                }}
                                onRefreshInvestmentMeasuresReady={(refreshFn) => {
                                  refreshInvestmentMeasuresRef.current = refreshFn
                                }}
                                onRefreshAgentPanelsReady={(refreshFn) => {
                                  refreshAgentPanelsRef.current = refreshFn
                                }}
                                onNavigateToAgentTab={() =>
                                  setActiveTab(accountHasPortfolioRole(currentCompany) ? agentTabKey : lpAgentTabKey)
                                }
                              />
                            </div>
                          )
                        },
                        {
                          key: detailsTabKey,
                          label: 'Details',
                          children: (
                            <div data-debug='full-form-details-tab' className='h-full'>
                              <DetailsTab
                                key={detailsTabKey}
                                currentCompany={currentCompany}
                                formData={formData}
                                handleFieldChange={handleFieldChange}
                                onPatchSuccess={handlePatchSuccess}
                                recordId={recordId}
                              />
                            </div>
                          )
                        },
                        {
                          key: contactsTabKey,
                          label: 'Contacts',
                          children: (
                            <div data-debug='full-form-contacts-tab' className='h-full'>
                              <ContactsTab
                                key={contactsTabKey}
                                formData={formData}
                                currentCompany={currentCompany}
                                companyTeamMembers={companyTeamMembersUnmerged}
                                handleFieldChange={handleFieldChange}
                                handleSaveField={handleSaveField}
                                recordId={recordId}
                              />
                            </div>
                          )
                        },
                        ...(showHoldingsTab
                          ? [
                              {
                                key: holdingsTabKey,
                                label: 'Holdings',
                                children: (
                                  <div data-debug='full-form-holdings-tab' className='h-full'>
                                    <HoldingsTab
                                      key={holdingsTabKey}
                                      recordId={recordId}
                                      accountName={currentCompany?.[AccountRecordFields.accounts__name]}
                                      onRefreshHoldingsReady={(refreshFn) => {
                                        refreshHoldingsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(showFundsTab
                          ? [
                              {
                                key: fundsTabKey,
                                label: 'Funds',
                                children: (
                                  <div data-debug='full-form-funds-tab' className='h-full'>
                                    <FundsTab
                                      key={fundsTabKey}
                                      recordId={recordId}
                                      accountName={currentCompany?.[AccountRecordFields.accounts__name]}
                                      onRefreshCommitmentsReady={(refreshFn) => {
                                        refreshCommitmentsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId
                          ? [
                              {
                                key: 'relationship',
                                label: 'Relationship',
                                children: (
                                  <div data-debug='full-form-relationship-tab' className='h-full'>
                                    <RelationshipTab
                                      recordId={recordId}
                                      currentCompany={currentCompany}
                                      formData={formData}
                                      handleFieldChange={handleFieldChange}
                                      onPatchSuccess={handlePatchSuccess}
                                      onRefreshReady={(refreshFn) => {
                                        refreshRelationshipTabRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId && showDealsRoundsTab
                          ? [
                              {
                                key: dealsRoundsTabKey,
                                label: 'Deals & Rounds',
                                children: (
                                  <div data-debug='full-form-deals-rounds-tab' className='h-full overflow-y-auto'>
                                    <AccountDealsRoundsTab
                                      key={dealsRoundsTabKey}
                                      accountId={recordId}
                                      accountName={currentCompany?.[AccountRecordFields.accounts__name]}
                                      onRefreshReady={(refreshFn: () => Promise<void>) => {
                                        refreshDealsRoundsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        // Agent tab (internal agents Phase 2, owner-placed
                        // after Deals & Rounds): the company follower's full
                        // picture. An account that is ALSO an LP gets a second
                        // Agent tab for the relationship manager (LP agent
                        // programme Phase 3), so the labels disambiguate.
                        ...(!isNew && recordId && accountHasPortfolioRole(currentCompany)
                          ? [
                              {
                                key: agentTabKey,
                                label: accountHasLpRole(currentCompany) ? 'Company Agent' : 'Agent',
                                children: (
                                  <div data-debug='full-form-agent-tab' className='h-full overflow-y-auto'>
                                    <AgentTab
                                      key={agentTabKey}
                                      subjectId={recordId}
                                      agentTypeKey='company_follower'
                                      onRefreshReady={(refreshFn) => {
                                        refreshAgentTabRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId && accountHasLpRole(currentCompany)
                          ? [
                              {
                                key: lpAgentTabKey,
                                label: accountHasPortfolioRole(currentCompany) ? 'LP Agent' : 'Agent',
                                children: (
                                  <div data-debug='full-form-lp-agent-tab' className='h-full overflow-y-auto'>
                                    <AgentTab
                                      key={lpAgentTabKey}
                                      subjectId={recordId}
                                      agentTypeKey='lp_relationship_manager'
                                      onRefreshReady={(refreshFn) => {
                                        refreshLpAgentTabRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId && showCapTableTab
                          ? [
                              {
                                key: capTableTabKey,
                                label: 'Cap Table',
                                children: (
                                  <div data-debug='full-form-cap-table-tab' className='h-full overflow-y-auto'>
                                    <CapTableTab
                                      key={capTableTabKey}
                                      accountId={recordId}
                                      onRefreshReady={(refreshFn) => {
                                        refreshCapTableRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId && showLegalRightsTab
                          ? [
                              {
                                key: legalRightsTabKey,
                                label: 'Legal Rights',
                                children: (
                                  <div data-debug='full-form-legal-rights-tab' className='h-full overflow-y-auto'>
                                    <LegalRightsTab
                                      key={legalRightsTabKey}
                                      accountId={recordId}
                                      onRefreshReady={(refreshFn) => {
                                        refreshLegalRightsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId
                          ? [
                              {
                                key: documentsTabKey,
                                label: 'Documents',
                                children: (
                                  <div data-debug='full-form-documents-tab' className='h-full overflow-y-auto'>
                                    <AccountDocumentsTab
                                      key={documentsTabKey}
                                      accountId={recordId}
                                      onRefreshReady={(refreshFn) => {
                                        refreshDocumentsRef.current = refreshFn
                                      }}
                                    />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(showCoInvestmentsTab && recordId
                          ? [
                              {
                                key: coInvestmentsTabKey,
                                label: 'Co-investments',
                                children: (
                                  <div data-debug='full-form-co-investments-tab' className='h-full overflow-y-auto'>
                                    <AccountCoInvestmentsTab key={coInvestmentsTabKey} accountId={recordId} />
                                  </div>
                                )
                              }
                            ]
                          : []),
                        ...(!isNew && recordId
                          ? [
                              {
                                key: topicsTabKey,
                                label: 'Communication',
                                children: (
                                  <div data-debug='full-form-topics-tab' className='h-full'>
                                    <AccountTopicsTab
                                      key={topicsTabKey}
                                      recordId={recordId}
                                      accountName={currentCompany?.[AccountRecordFields.accounts__name]}
                                    />
                                  </div>
                                )
                              },
                              {
                                key: changeLogTabKey,
                                label: 'Change Log',
                                children: (
                                  <div data-debug='full-form-change-log-tab' className='h-full'>
                                    <ChangeLogTab key={changeLogTabKey} contextTable='accounts' recordId={recordId} />
                                  </div>
                                )
                              }
                            ]
                          : [])
                      ]}
                    />
                  </Col>
                </Row>
              </Form>
            </Card>
          </div>
        </div>
      </div>
    </DndProvider>
  )
}
